/**
 * Static Data — provides pre-fetched data to pages during SSR and client hydration.
 *
 * During static export:  data comes from the build-time API calls via context.
 * During client hydration: data is read from window.__STATIC_DATA__ injected in the HTML.
 * During normal dev:       no static data exists, pages fall back to runtime API fetching.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

const StaticDataContext = createContext<Record<string, any>>({});

export function StaticDataProvider({ data, children }: { data: Record<string, any>; children: ReactNode }) {
  return <StaticDataContext.Provider value={data}>{children}</StaticDataContext.Provider>;
}

/**
 * Read pre-fetched static data by key.
 * Returns the data if it was fetched at build time, or undefined if not available.
 */
export function useStaticData<T = any>(key: string): T | undefined {
  return useContext(StaticDataContext)[key] as T | undefined;
}

/**
 * Read static data from window.__STATIC_DATA__ (injected by the export script).
 * Returns an empty object when running in dev or during SSR.
 */
export function getInitialStaticData(): Record<string, any> {
  if (typeof window !== "undefined" && (window as any).__STATIC_DATA__) {
    return (window as any).__STATIC_DATA__;
  }
  return {};
}
